import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
// NOTE: do NOT import `usePrimeVue` here. PrimeVue 4 requires it to be called
// inside a setup() / component lifecycle (it reads getCurrentInstance()),
// and main.ts runs at the app bootstrap level — calling it there throws
// "PrimeVue is not installed!". We reach into `app.config.globalProperties.$primevue`
// directly, which is the same object usePrimeVue() returns at runtime.
import NeoGoPreset from './theme/neogo-preset'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import Tooltip from 'primevue/tooltip'
import router from './router'
import App from './App.vue'
import { i18n, type SupportedLocale } from './i18n'
import { getPrimeVueLocale } from './i18n/primevue-locales'
import { setPrimeVueLocaleApplier } from './composables/useLocale'
import { startTelemetry, recordPageLoad, recordPageError, setCurrentRoute } from './lib/telemetry'
import { initSentry, captureUserContext } from './lib/sentry'
import { initWebVitals } from './lib/web-vitals'
import { useNotificationsStore } from './stores/notifications'
import { useUserStore } from './stores/user'

import 'primeicons/primeicons.css'
// Tailwind v4 + tailwindcss-primeui replaced PrimeFlex in Phase 2 (see
// theme/tailwind.css for the `primeflex-shim` cascade layer that
// preserves the surface-*, border-round*, shadow-1..5, field, and
// flex-based grid/col-N utilities PrimeFlex used to ship).
import './theme/tailwind.css'
import './theme/neogo-theme.css'
import './theme/dark-mode-small.css'
import './theme/dark-mode-medium.css'

const app = createApp(App)

app.use(createPinia())
// vue-i18n must be installed before PrimeVue/router because some templates
// run `t()` during early-mount (e.g. the AppLayout's impersonation banner).
app.use(i18n)

// Initial PrimeVue locale matches the bootstrap vue-i18n locale so months,
// paginator labels, accept/reject buttons, etc. render in the right language
// from the first paint.
const initialLocale = i18n.global.locale.value as SupportedLocale
app.use(PrimeVue, {
  locale: getPrimeVueLocale(initialLocale),
  theme: {
    preset: NeoGoPreset,
    options: {
      darkModeSelector: '.dark-mode',
    },
  },
})

// Wire `useLocale().setLocale()` into PrimeVue's runtime locale. We mutate
// the existing config.locale object so PrimeVue's shared reactive reference
// propagates to every mounted component (Calendar, Paginator, Tree, etc.).
//
// PrimeVue 4 exposes the same object via `app.config.globalProperties.$primevue`
// that `usePrimeVue()` returns inside components. Reading it here (outside any
// setup()) avoids the `getCurrentInstance() === null` crash at boot.
type PrimeVueGlobal = { config: { locale?: Record<string, unknown> } }
const pvGlobals = app.config.globalProperties as unknown as {
  $primevue?: PrimeVueGlobal
}
setPrimeVueLocaleApplier((code) => {
  const pv = pvGlobals.$primevue
  if (!pv || !pv.config || !pv.config.locale) return
  const next = getPrimeVueLocale(code)
  // Replace entries on the existing locale object to preserve reactivity —
  // PrimeVue components read from the shared `config.locale` reference, so
  // mutating in place propagates without remounting anything.
  const target = pv.config.locale
  for (const key of Object.keys(target)) {
    delete target[key]
  }
  Object.assign(target, next)
})

app.directive('tooltip', Tooltip)
app.use(ToastService)
app.use(ConfirmationService)
app.use(router)

// Sentry SDK (NEOA-frontend-obs): captures uncaught exceptions with stack
// traces + session replays for any crash. No-op when VITE_SENTRY_DSN is
// empty so dev / pre-config deploys stay zero-overhead.
initSentry(app, router)

// Bridge user identity into the Sentry scope so events can be filtered by
// user/org. Pinia store hydrates async (after /auth/me), so we use a watcher.
const userStore = useUserStore()
watch(
  () => userStore.user,
  (u) => {
    if (u) {
      captureUserContext({
        id: u.id,
        email: u.email,
        orgId: u.organizationId,
      })
    } else {
      captureUserContext(null)
    }
  },
  { immediate: true },
)

// Global Vue error handler (NEOA-8 #1): without this Vue silently swallows
// runtime errors thrown inside lifecycle hooks/watchers, leaving the user
// staring at a half-rendered view. We push the error into the toast queue
// so the user sees something, and into RUM telemetry so backend metrics
// pick it up.
app.config.errorHandler = (err, _instance, info) => {
  const message = err instanceof Error ? err.message : String(err)

  // PrimeVue 4 quirk: Menu/Popover/OverlayPanel's alignOverlay reads
  // offsetHeight on a trigger element captured during click. When a Pinia
  // action fired from the same handler triggers a re-render before the
  // transition's onEnter runs, the trigger ref can be null and this throws
  // "TypeError: can't access property 'offsetHeight', t is null".
  // The toggle still completes visually (user sees the menu); the throw is
  // cosmetic. Swallow it to keep the user-facing console clean.
  if (message.includes('offsetHeight') && typeof err === 'object' && err !== null) {
    const stack = (err as Error).stack || ''
    if (stack.includes('alignOverlay') || stack.includes('onEnter')) {
      // Still record to telemetry so we can see the rate, but don't toast.
      recordPageError(null, 'primevue-overlay-align')
      return
    }
  }

  // Pinia stores can be safely created lazily after app.use(createPinia()).
  try {
    const notifications = useNotificationsStore()
    notifications.error('Algo deu errado', message)
  } catch {
    // Notifications store unavailable (eg. during very early boot) — fall
    // back to console so dev still sees it.
    console.error('[vue:error]', err, info)
  }
  recordPageError(null, info?.includes('render') ? 'render' : 'uncaught')
  // Re-log to console for dev visibility.
  console.error('[vue:error]', err, info)
}

// RUM telemetry (NEOA-26): record route name + nav-to-load duration so
// /manager-api/telemetry can aggregate Manager UX health into Prometheus.
let routeStartedAt = 0
router.beforeEach((to, _from, next) => {
  routeStartedAt = performance.now()
  setCurrentRoute(typeof to.name === 'string' ? to.name : null)
  next()
})
router.afterEach((to) => {
  if (routeStartedAt > 0) {
    recordPageLoad(typeof to.name === 'string' ? to.name : null, performance.now() - routeStartedAt)
  }
})
startTelemetry()

// Core Web Vitals (LCP, INP, CLS, FCP, TTFB). Forwards to Sentry as
// breadcrumbs/measurements when DSN is configured. A future change can
// pipe these into Prometheus via a `recordWebVital(metric)` callback —
// for now we keep this Sentry-only (see SENTRY.md for rationale).
initWebVitals()

app.mount('#app')

// Fast-path deploy trigger: force frontend update
